import { useApolloClient } from "@apollo/client";
import { CardContainer } from "@msys/ui";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { useSnackbar } from "notistack";
import { useEffect, useRef } from "react";
import { AutoSave } from "../../../commons/form-fields/AutoSave.js";
import { SelectField } from "../../../commons/form-fields/SelectField.js";
import { SwitchField } from "../../../commons/form-fields/SwitchField.js";
import {
  EInvoiceFormat,
  UpdateOrganisationEInvoiceSettingsInput,
} from "../../../../clients/graphqlTypes.js";
import {
  OrganisationEInvoiceSettingsBox_ViewerOrganisationFragment,
  useOrganisationEInvoiceSettingsBox_UpdateOrganisationEInvoiceSettingsMutation,
} from "./OrganisationEInvoiceSettingsBox.generated.js";

// Mirrors `DEFAULT_E_INVOICE_FORMAT` in `@msys/e-invoice` — kept as a local
// literal because importing the lib barrel would pull the node-only XML/PDF
// generation modules (`@e-invoice-eu/core`, Buffer) into the browser bundle.
// `satisfies` keeps the value pinned to the schema enum.
const DEFAULT_FORMAT = "ZUGFERD_XRECHNUNG" satisfies EInvoiceFormat;

interface Props {
  organisation: OrganisationEInvoiceSettingsBox_ViewerOrganisationFragment;
}

interface FormValues {
  enabled: boolean;
  format: EInvoiceFormat | "";
}

const MISSING_FIELD_LABELS = {
  billingAddress: "missingField.billingAddress",
  format: "missingField.format",
  fullLegalName: "missingField.fullLegalName",
  iban: "missingField.iban",
  vatOrTaxNumber: "missingField.vatOrTaxNumber",
} as const;

const FORMAT_OPTIONS: { value: EInvoiceFormat; labelKey: string }[] = [
  { value: "ZUGFERD_XRECHNUNG", labelKey: "ZUGFeRD XRechnung" },
  { value: "ZUGFERD_EN16931", labelKey: "ZUGFeRD EN 16931" },
  { value: "ZUGFERD_EXTENDED", labelKey: "ZUGFeRD Extended" },
  { value: "XRECHNUNG_CII", labelKey: "XRechnung (CII)" },
  { value: "XRECHNUNG_UBL", labelKey: "XRechnung (UBL)" },
];

export function OrganisationEInvoiceSettingsBox({ organisation }: Props) {
  const { t } = useTranslate("OrganisationSettings");
  return (
    <CardContainer isExpandable title={t("E-invoice settings")}>
      <Box sx={{ p: 1 }}>
        <OrganisationEInvoiceSettingsForm organisation={organisation} />
      </Box>
    </CardContainer>
  );
}

function AutoSelectDefaultFormat() {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const prevEnabled = useRef(values.enabled);

  useEffect(() => {
    if (values.enabled && !prevEnabled.current && !values.format) {
      void setFieldValue("format", DEFAULT_FORMAT);
    }
    prevEnabled.current = values.enabled;
  }, [values.enabled, values.format, setFieldValue]);

  return null;
}

function OrganisationEInvoiceSettingsForm({ organisation }: Props) {
  const { t } = useTranslate("OrganisationSettings");
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [updateSettings] =
    useOrganisationEInvoiceSettingsBox_UpdateOrganisationEInvoiceSettingsMutation(
      { client }
    );

  const { enabled, format, validationStatus } = organisation.eInvoiceSettings;
  const initialValues: FormValues = {
    enabled,
    format: format ?? "",
  };

  const isLockedFromEnabling = !validationStatus.canEnable;

  const handleSubmit = async (values: FormValues) => {
    try {
      const input: UpdateOrganisationEInvoiceSettingsInput = {
        enabled: values.enabled,
        format: values.enabled && values.format ? values.format : null,
      };
      await updateSettings({ variables: { input } });
    } catch {
      enqueueSnackbar(t("An error occurred."), { variant: "error" });
    }
  };

  return (
    <Formik<FormValues> initialValues={initialValues} onSubmit={handleSubmit}>
      {({ values }) => {
        const isAutoSaveDisabled =
          (values.enabled && !values.format) ||
          (values.enabled && isLockedFromEnabling);
        return (
          <Form>
            <Stack spacing={2}>
              <AutoSelectDefaultFormat />

              <SwitchField
                name="enabled"
                label={t("Enable e-invoicing")}
                disabled={isLockedFromEnabling && !values.enabled}
              />

              {isLockedFromEnabling ? (
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    {t(
                      values.enabled
                        ? "Please complete the following fields:"
                        : "To enable, complete:"
                    )}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.5 }}>
                    {validationStatus.missingFields.map(field => {
                      const key =
                        MISSING_FIELD_LABELS[
                          field as keyof typeof MISSING_FIELD_LABELS
                        ];
                      return (
                        <Typography
                          key={field}
                          component="li"
                          variant="caption"
                          color="textSecondary"
                        >
                          {key ? t(key) : field}
                        </Typography>
                      );
                    })}
                  </Box>
                </Box>
              ) : null}

              <SelectField
                name="format"
                label={t("E-invoice format")}
                disabled={!values.enabled}
                options={FORMAT_OPTIONS.map(({ value, labelKey }) => ({
                  value,
                  label: labelKey,
                }))}
              />

              <AutoSave isAutoSaveDisabled={isAutoSaveDisabled} />
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}
